import { useEffect, useState } from 'react';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import type { StoryTemplateDoc } from '@spirits/shared';
import {
  auth,
  functions,
  signInWithGoogle,
  signInAsLocalAdmin,
  signOutToAnonymous,
  IS_LOCALHOST,
} from '../firebase';
import { useCollection } from '../hooks/useCollection';
import { TemplateEditor } from './TemplateEditor';

const ADMIN_DOMAIN = 'sunbisoft.com';

const seedTemplatesCallable = httpsCallable<unknown, { ok: boolean; templates: number }>(
  functions,
  'seedTemplates',
);
const wipeWorldCallable = httpsCallable<unknown, { ok: boolean; deleted: number }>(
  functions,
  'wipeWorld',
);

function isAdminEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith(`@${ADMIN_DOMAIN}`);
}

export default function AdminRoute() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUser(u);
    setAuthReady(true);
  }), []);

  if (!authReady) {
    return <Centered>checking credentials…</Centered>;
  }

  const email = user?.email ?? null;
  if (!isAdminEmail(email)) {
    return <AuthGate signedInEmail={email} />;
  }

  return <AdminDashboard email={email!} />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400 text-sm">
      {children}
    </div>
  );
}

function AuthGate({ signedInEmail }: { signedInEmail: string | null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (e) {
      setError((e as Error)?.message ?? 'sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  async function switchAccount() {
    setBusy(true);
    setError(null);
    try {
      await signOutToAnonymous();
    } catch (e) {
      setError((e as Error)?.message ?? 'sign-out failed');
    } finally {
      setBusy(false);
    }
  }

  async function devLogin() {
    setBusy(true);
    setError(null);
    try {
      await signInAsLocalAdmin();
    } catch (e) {
      setError((e as Error)?.message ?? 'dev login failed');
    } finally {
      setBusy(false);
    }
  }

  // signedInEmail set but not on the domain → a non-admin Google account.
  const wrongAccount = !!signedInEmail;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-center">
        <h1 className="text-xl text-slate-100">Spirits Admin</h1>
        {wrongAccount ? (
          <>
            <p className="mt-2 text-sm text-rose-300">
              {signedInEmail} isn't a @{ADMIN_DOMAIN} account.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Admin access is limited to {ADMIN_DOMAIN} accounts.
            </p>
            <button
              type="button"
              onClick={switchAccount}
              disabled={busy}
              className="mt-4 w-full rounded-lg border border-slate-700 text-slate-300 hover:text-slate-100 hover:border-slate-500 px-4 py-2 text-sm disabled:opacity-50"
            >
              {busy ? 'switching…' : 'Use a different account'}
            </button>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400">
              Sign in with your {ADMIN_DOMAIN} Google account to manage story templates.
            </p>
            <button
              type="button"
              onClick={signIn}
              disabled={busy}
              className="mt-4 w-full rounded-lg border border-amber-400/60 text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 px-4 py-2 text-sm disabled:opacity-50"
            >
              {busy ? 'opening…' : 'Sign in with Google'}
            </button>
          </>
        )}
        {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}
        {IS_LOCALHOST && (
          <button
            type="button"
            onClick={devLogin}
            disabled={busy}
            className="mt-4 w-full rounded-lg border border-dashed border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500 px-4 py-2 text-xs disabled:opacity-50"
          >
            dev: sign in as {`dev@${ADMIN_DOMAIN}`} (localhost only)
          </button>
        )}
        <Link to="/" className="mt-4 inline-block text-xs text-slate-500 hover:text-slate-300">
          ← back to Spirits
        </Link>
      </div>
    </div>
  );
}

function AdminDashboard({ email }: { email: string }) {
  const { data: templates, loading } = useCollection<StoryTemplateDoc>('storyTemplates');
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <header className="flex-none px-6 py-3 border-b border-slate-900 flex items-center justify-between gap-6">
        <div className="flex items-baseline gap-3">
          <Link to="/" className="text-2xl tracking-wide text-slate-100 hover:text-amber-100">
            Spirits
          </Link>
          <Link
            to="/admin"
            className="text-xs uppercase tracking-widest text-amber-200/80 hover:text-amber-100"
          >
            admin
          </Link>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500 hidden sm:inline">{email}</span>
          <Link to="/" className="text-slate-400 hover:text-slate-200">
            ← lobby
          </Link>
          <button
            type="button"
            onClick={() => signOutToAnonymous()}
            className="text-slate-400 hover:text-slate-200"
          >
            sign out
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">
        {/* Editor open/close is routed (/admin/new, /admin/:id) so the browser
            back button returns to the list rather than leaving /admin. */}
        <Routes>
          <Route
            index
            element={
              <div className="space-y-8">
                <WorldManagement />
                <TemplateList templates={templates} loading={loading} />
              </div>
            }
          />
          <Route
            path="new"
            element={<TemplateEditor existing={null} onClose={() => navigate('/admin')} />}
          />
          <Route
            path=":templateId"
            element={
              <EditTemplate templates={templates} loading={loading} onClose={() => navigate('/admin')} />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function TemplateList({
  templates,
  loading,
}: {
  templates: (StoryTemplateDoc & { id: string })[];
  loading: boolean;
}) {
  const navigate = useNavigate();
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg text-slate-300">Story templates</h2>
        <button
          type="button"
          onClick={() => navigate('/admin/new')}
          className="rounded-lg border border-amber-400/60 text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 px-4 py-1.5 text-sm"
        >
          + New template
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">loading templates…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 p-6 text-slate-400 text-sm">
          No templates yet. Create one, or use <span className="font-mono">Seed templates</span> above to
          write the hard-coded catalog.
        </div>
      ) : (
        <div className="space-y-2">
          {[...templates]
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((t) => (
              <TemplateRow key={t.id} template={t} onEdit={() => navigate(`/admin/${t.id}`)} />
            ))}
        </div>
      )}
    </section>
  );
}

function EditTemplate({
  templates,
  loading,
  onClose,
}: {
  templates: (StoryTemplateDoc & { id: string })[];
  loading: boolean;
  onClose: () => void;
}) {
  const { templateId } = useParams<{ templateId: string }>();
  const template = templates.find((t) => t.id === templateId) ?? null;

  if (!template) {
    if (loading) return <p className="text-slate-500 text-sm">loading template…</p>;
    return (
      <div className="rounded-2xl border border-dashed border-slate-700 p-6 text-slate-400 text-sm">
        No template with id <span className="font-mono">{templateId}</span>.{' '}
        <button
          type="button"
          onClick={onClose}
          className="text-amber-200/90 hover:text-amber-100 underline-offset-2 hover:underline"
        >
          back to list
        </button>
      </div>
    );
  }

  return <TemplateEditor existing={template} onClose={onClose} />;
}

function TemplateRow({
  template,
  onEdit,
}: {
  template: StoryTemplateDoc & { id: string };
  onEdit: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full text-left rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 transition-colors hover:border-slate-600 flex items-center justify-between gap-4"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-slate-100">{template.title}</span>
          <span className="text-[10px] uppercase tracking-widest text-amber-200/70">{template.category}</span>
        </div>
        <p className="text-xs text-slate-500 font-mono truncate">{template.id}</p>
      </div>
      <div className="flex-none text-[11px] text-slate-500 flex items-center gap-3">
        <span>{template.cast?.length ?? 0} cast</span>
        <span>{template.scenes?.length ?? 0} scenes</span>
        <span>{template.endings?.length ?? 0} endings</span>
        <span className="text-amber-200/70">edit →</span>
      </div>
    </button>
  );
}

function WorldManagement() {
  const [busy, setBusy] = useState<null | 'seed' | 'wipe'>(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function seed() {
    if (busy) return;
    setBusy('seed');
    setMessage(null);
    setError(null);
    try {
      const res = await seedTemplatesCallable({});
      setMessage(`Seeded ${res.data.templates} templates from code.`);
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'seed failed');
    } finally {
      setBusy(null);
    }
  }

  async function wipe() {
    if (busy) return;
    setBusy('wipe');
    setConfirmWipe(false);
    setMessage(null);
    setError(null);
    try {
      const res = await wipeWorldCallable({});
      setMessage(`Deleted ${res.data.deleted} docs. Player accounts were kept.`);
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'wipe failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
      <h2 className="text-lg text-slate-300">World management</h2>
      <p className="mt-1 text-xs text-slate-500">
        Server actions. Seeding rewrites the hard-coded template catalog; deleting wipes all generated
        world data (templates, runs, scenes, bots, news, recaps, prompts) but keeps player accounts.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={seed}
          disabled={!!busy}
          className="rounded-lg border border-amber-400/50 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 px-4 py-1.5 text-sm disabled:opacity-50"
        >
          {busy === 'seed' ? 'seeding…' : 'Seed templates'}
        </button>

        {confirmWipe ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-rose-300">Delete all world data?</span>
            <button
              type="button"
              onClick={wipe}
              disabled={!!busy}
              className="px-3 py-1.5 rounded-lg border border-rose-700/60 text-rose-300 hover:bg-rose-900/30 disabled:opacity-50"
            >
              {busy === 'wipe' ? 'deleting…' : 'yes, delete everything'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmWipe(false)}
              className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200"
            >
              cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmWipe(true)}
            disabled={!!busy}
            className="rounded-lg border border-rose-900/60 text-rose-400/80 hover:text-rose-300 hover:border-rose-700/60 px-4 py-1.5 text-sm disabled:opacity-50"
          >
            Delete all data
          </button>
        )}
      </div>

      {message && <p className="mt-3 text-xs text-emerald-400">{message}</p>}
      {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}
    </section>
  );
}
